import React, { useState } from 'react';
import useGrid from '../hooks/useGrid';
import usePlants from '../hooks/usePlants';
import SyncConfigSection from './SyncConfigSection';
import { seedSpeciesFromInventory, clearAndReseedSpecies } from '../services/seedData';
import { backfillFromTrefle } from '../services/trefleService';
import { backfillWetlandRatings } from '../services/wetlandBackfill';
import { sharedDb, recordChange } from '@monorepo/shared';
import db from '../services/db';
import './GridConfigView.css';

function GridConfigView() {
  const { gridConfig, resizeGrid, confirmResize } = useGrid();
  const { loadPlants, species, plants } = usePlants();

  const [newRows, setNewRows] = useState('');
  const [newCols, setNewCols] = useState('');
  const [orphanedPlants, setOrphanedPlants] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [seedMessage, setSeedMessage] = useState('');
  const [seeding, setSeeding] = useState(false);

  const handleSeedSpecies = async (clear = false) => {
    setSeeding(true);
    setSeedMessage('');
    try {
      let count;
      if (clear) {
        count = await clearAndReseedSpecies();
      } else {
        count = await seedSpeciesFromInventory();
      }
      await loadPlants();
      setSeedMessage(count > 0 ? `Added ${count} new species to the catalog.` : 'No new species to add.');
    } catch (err) {
      setSeedMessage(`Error: ${err.message}`);
    }
    setSeeding(false);
  };

  const handleResize = async () => {
    setSuccessMessage('');
    const rows = parseInt(newRows, 10) || (gridConfig ? gridConfig.rows : 4);
    const cols = parseInt(newCols, 10) || (gridConfig ? gridConfig.cols : 4);

    const orphans = await resizeGrid(rows, cols);

    if (orphans && orphans.length > 0) {
      setOrphanedPlants(orphans);
      setShowDialog(true);
    } else {
      await confirmResize(rows, cols);
      setSuccessMessage(`Grid resized to ${rows} rows × ${cols} columns.`);
    }
  };

  const handleConfirmResize = async () => {
    const rows = parseInt(newRows, 10) || (gridConfig ? gridConfig.rows : 4);
    const cols = parseInt(newCols, 10) || (gridConfig ? gridConfig.cols : 4);
    await confirmResize(rows, cols);
    setShowDialog(false);
    setOrphanedPlants(null);
    setSuccessMessage(`Grid resized to ${rows} rows × ${cols} columns.`);
  };

  const handleCancelResize = () => {
    setShowDialog(false);
    setOrphanedPlants(null);
  };

  if (!gridConfig) {
    return <div className="grid-config-view">Loading configuration...</div>;
  }

  return (
    <div className="grid-config-view">
      <h2 className="grid-config-view__title">Grid Configuration</h2>
      <p className="grid-config-view__current">
        Current dimensions: {gridConfig.rows} rows × {gridConfig.cols} columns
      </p>

      <div className="grid-config-view__form">
        <div className="grid-config-view__field">
          <label htmlFor="grid-rows">Rows</label>
          <input
            id="grid-rows"
            type="number"
            min="1"
            max="20"
            value={newRows}
            placeholder={String(gridConfig.rows)}
            onChange={(e) => setNewRows(e.target.value)}
            aria-label="Number of rows"
          />
        </div>

        <div className="grid-config-view__field">
          <label htmlFor="grid-cols">Columns</label>
          <input
            id="grid-cols"
            type="number"
            min="1"
            max="20"
            value={newCols}
            placeholder={String(gridConfig.cols)}
            onChange={(e) => setNewCols(e.target.value)}
            aria-label="Number of columns"
          />
        </div>

        <button
          className="grid-config-view__resize-btn"
          onClick={handleResize}
        >
          Resize Grid
        </button>
      </div>

      {successMessage && (
        <div className="grid-config-view__success" role="status">
          {successMessage}
        </div>
      )}

      {showDialog && (
        <div className="grid-config-view__dialog-overlay">
          <div
            className="grid-config-view__dialog"
            role="dialog"
            aria-labelledby="orphan-dialog-title"
            aria-modal="true"
          >
            <h3
              id="orphan-dialog-title"
              className="grid-config-view__dialog-title"
            >
              Warning: Orphaned Plants
            </h3>
            <p className="grid-config-view__dialog-message">
              The following plants will be outside the new grid dimensions and
              may be lost:
            </p>
            <ul className="grid-config-view__orphan-list">
              {orphanedPlants.map((plant) => (
                <li key={plant.id}>
                  {plant.commonName} (Row {plant.gridRow + 1}, Col{' '}
                  {plant.gridCol + 1})
                </li>
              ))}
            </ul>
            <div className="grid-config-view__dialog-actions">
              <button
                className="grid-config-view__dialog-cancel"
                onClick={handleCancelResize}
              >
                Cancel
              </button>
              <button
                className="grid-config-view__dialog-confirm"
                onClick={handleConfirmResize}
              >
                Confirm Resize
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid-config-view__seed-section">
        <h3 className="grid-config-view__section-title">Species Catalog</h3>
        <p className="grid-config-view__section-desc">
          Seed the species catalog from the plant inventory database. This adds species that aren't already in the catalog.
        </p>
        <p className="grid-config-view__section-desc">
          <strong>{species.length}</strong> species in catalog · <strong>{plants.length}</strong> plant instances in grid
        </p>
        <button
          className="grid-config-view__seed-btn"
          onClick={() => handleSeedSpecies(false)}
          disabled={seeding}
        >
          {seeding ? 'Seeding...' : 'Add New Species from Inventory'}
        </button>
        <button
          className="grid-config-view__seed-btn grid-config-view__seed-btn--danger"
          onClick={() => handleSeedSpecies(true)}
          disabled={seeding}
        >
          Clear & Re-seed All
        </button>
        {seedMessage && (
          <p className="grid-config-view__seed-message" role="status">{seedMessage}</p>
        )}
      </div>

      <BackfillSection loadPlants={loadPlants} />

      <SyncConfigSection />

      <PlantNetConfigSection />
    </div>
  );
}

function BackfillSection({ loadPlants }) {
  const [trefleStatus, setTrefleStatus] = useState('');
  const [trefleRunning, setTrefleRunning] = useState(false);
  const [trefleToken, setTrefleToken] = useState('');
  const [tokenSaved, setTokenSaved] = useState(false);
  const [wetlandStatus, setWetlandStatus] = useState('');
  const [wetlandRunning, setWetlandRunning] = useState(false);
  const [usdaStatus, setUsdaStatus] = useState('');
  const [usdaRunning, setUsdaRunning] = useState(false);
  const [geminiKey, setGeminiKey] = useState(() => localStorage.getItem('gemini-api-key') || '');
  const [geminiKeySaved, setGeminiKeySaved] = useState(false);

  // Load saved token on mount
  useState(() => {
    sharedDb.syncConfig.get('sync-config').then(config => {
      if (config?.trefleToken) setTrefleToken(config.trefleToken);
    });
  });

  const handleSaveToken = async () => {
    const config = await sharedDb.syncConfig.get('sync-config') || { id: 'sync-config' };
    await sharedDb.syncConfig.put({ ...config, trefleToken });
    setTokenSaved(true);
    setTimeout(() => setTokenSaved(false), 3000);
  };

  const handleTrefleBackfill = async () => {
    setTrefleRunning(true);
    setTrefleStatus('Fetching data from Trefle... this may take a while.');
    try {
      const config = await sharedDb.syncConfig.get('sync-config');
      const token = config?.trefleToken || trefleToken;
      if (!token) {
        setTrefleStatus('Error: Trefle API token not set. Enter it above and save.');
        setTrefleRunning(false);
        return;
      }
      const result = await backfillFromTrefle(sharedDb, recordChange, token, db);
      if (result.error) {
        setTrefleStatus(result.error);
      } else {
        setTrefleStatus(`Done. Updated: ${result.updated}, Skipped: ${result.skipped}, Not found: ${result.failed}`);
      }
      await loadPlants();
    } catch (err) {
      setTrefleStatus(`Error: ${err.message}`);
    }
    setTrefleRunning(false);
  };

  const handleWetlandBackfill = async () => {
    setWetlandRunning(true);
    setWetlandStatus('Looking up wetland ratings...');
    try {
      const result = await backfillWetlandRatings();
      if (result.error) {
        setWetlandStatus(result.error);
      } else {
        setWetlandStatus(`Done. Updated: ${result.updated}, Not found: ${result.notFound}, Skipped: ${result.skipped}`);
        await loadPlants();
      }
    } catch (err) {
      setWetlandStatus(`Error: ${err.message}`);
    }
    setWetlandRunning(false);
  };

  const handleSaveGeminiKey = () => {
    localStorage.setItem('gemini-api-key', geminiKey);
    setGeminiKeySaved(true);
    setTimeout(() => setGeminiKeySaved(false), 3000);
  };

  const handleUsdaBackfill = async () => {
    setUsdaRunning(true);
    setUsdaStatus('Looking up USDA characteristics...');
    try {
      const { backfillUsdaCharacteristics } = await import('../services/usdaBackfill');
      const result = await backfillUsdaCharacteristics();
      if (result.error) {
        setUsdaStatus(result.error);
      } else {
        setUsdaStatus(`Done. Updated: ${result.updated}, Not found: ${result.notFound}, Skipped: ${result.skipped}`);
        await loadPlants();
      }
    } catch (err) {
      setUsdaStatus(`Error: ${err.message}`);
    }
    setUsdaRunning(false);
  };

  return (
    <div className="grid-config-view__seed-section">
      <h3 className="grid-config-view__section-title">Data Backfill</h3>
      <p className="grid-config-view__section-desc">
        Fill in missing species data from external sources. Only updates fields that are currently empty.
      </p>

      <div className="grid-config-view__backfill-row">
        <div className="grid-config-view__backfill-token">
          <label htmlFor="trefle-token-input"><strong>Trefle API Token</strong></label>
          <div className="grid-config-view__token-row">
            <input
              id="trefle-token-input"
              type="text"
              className="grid-config-view__token-input"
              value={trefleToken}
              onChange={(e) => setTrefleToken(e.target.value)}
              placeholder="Your Trefle.io API token"
            />
            <button className="grid-config-view__seed-btn" onClick={handleSaveToken} disabled={!trefleToken.trim()}>
              Save Token
            </button>
            {tokenSaved && <span className="grid-config-view__saved-indicator">✓</span>}
          </div>
        </div>
      </div>

      <div className="grid-config-view__backfill-row">
        <div>
          <strong>Trefle.io</strong> — Light, moisture, soil, bloom data
          <br />
          <small>Uses the Trefle botanical API. ~0.5s per species.</small>
        </div>
        <button
          className="grid-config-view__seed-btn"
          onClick={handleTrefleBackfill}
          disabled={trefleRunning}
        >
          {trefleRunning ? 'Running...' : 'Backfill from Trefle'}
        </button>
      </div>
      {trefleStatus && <p className="grid-config-view__seed-message" role="status">{trefleStatus}</p>}

      <div className="grid-config-view__backfill-row" style={{ marginTop: '16px' }}>
        <div>
          <strong>NWPL Wetland Ratings</strong> — AGCP & EMP ratings
          <br />
          <small>Requires running <code>node scripts/parse-nwpl.js</code> first.</small>
        </div>
        <button
          className="grid-config-view__seed-btn"
          onClick={handleWetlandBackfill}
          disabled={wetlandRunning}
        >
          {wetlandRunning ? 'Running...' : 'Backfill Wetland Ratings'}
        </button>
      </div>
      {wetlandStatus && <p className="grid-config-view__seed-message" role="status">{wetlandStatus}</p>}

      <div className="grid-config-view__backfill-row" style={{ marginTop: '16px' }}>
        <div>
          <strong>USDA PLANTS</strong> — Root depth, spread rate, drought & flood tolerance, salt tolerance
          <br />
          <small>Requires running <code>node scripts/parse-usda-plants.js</code> first.</small>
        </div>
        <button
          className="grid-config-view__seed-btn"
          onClick={handleUsdaBackfill}
          disabled={usdaRunning}
        >
          {usdaRunning ? 'Running...' : 'Backfill USDA Data'}
        </button>
      </div>
      {usdaStatus && <p className="grid-config-view__seed-message" role="status">{usdaStatus}</p>}

      <div className="grid-config-view__backfill-row" style={{ marginTop: '16px' }}>
        <div className="grid-config-view__backfill-token">
          <label htmlFor="gemini-key-input"><strong>Gemini API Key</strong></label>
          <p className="grid-config-view__section-desc" style={{ margin: '4px 0 8px' }}>
            For AI-assisted backfill of missing plant data.
            Get a key at <a href="https://aistudio.google.com/apikey" target="_blank" rel="noopener noreferrer">Google AI Studio</a>.
          </p>
          <div className="grid-config-view__token-row">
            <input
              id="gemini-key-input"
              type="password"
              className="grid-config-view__token-input"
              value={geminiKey}
              onChange={(e) => setGeminiKey(e.target.value)}
              placeholder="Your Gemini API key"
            />
            <button className="grid-config-view__seed-btn" onClick={handleSaveGeminiKey} disabled={!geminiKey.trim()}>
              Save Key
            </button>
            {geminiKeySaved && <span className="grid-config-view__saved-indicator">✓</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

function PlantNetConfigSection() {
  const [apiKey, setApiKey] = useState(() => localStorage.getItem('plantnet-api-key') || '');
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    localStorage.setItem('plantnet-api-key', apiKey);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <div className="grid-config-view__seed-section">
      <h3 className="grid-config-view__section-title">PlantNet API</h3>
      <p className="grid-config-view__section-desc">
        Enter your PlantNet API key to enable plant identification from photos.
        Get a key at <a href="https://my.plantnet.org/" target="_blank" rel="noopener noreferrer">my.plantnet.org</a>.
      </p>
      <div className="grid-config-view__field">
        <label htmlFor="plantnet-api-key">API Key</label>
        <input
          id="plantnet-api-key"
          type="password"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          placeholder="Your PlantNet API key"
          className="grid-config-view__input"
        />
      </div>
      <button className="grid-config-view__btn" onClick={handleSave}>
        Save Key
      </button>
      {saved && <p className="grid-config-view__success">Key saved.</p>}
    </div>
  );
}

export default GridConfigView;
